import { DEFAULT_ENTITY_LIFECYCLE_STAGES } from '../crm-stage'
import { FieldTypeEnum } from '../custom-field.types'
import { Entity } from '../entity'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'
import { EntityTerminology } from '../organization-settings'

/**
 * Standard width categories for consistent column sizing across all entity tables
 */
const ENTITY_COLUMN_WIDTHS = {
  // Very narrow - icons, small numbers, boolean indicators
  VERY_NARROW: { width: '50px', minWidth: '35px' },
  // Narrow - short text, priorities, small dropdowns
  NARROW: { width: '120px', minWidth: '80px' },
  // Medium - standard text fields, dates, status fields
  MEDIUM: { width: '160px', minWidth: '120px' },
  // Wide - longer text fields, descriptions, addresses
  WIDE: { width: '200px', minWidth: '140px' },
  // Very wide - names, main content
  VERY_WIDE: { width: '280px', minWidth: '200px' },
} as const

/**
 * Creates standard column definitions for the Entity module with proper terminology
 * @param terminology - The entity terminology to use for labels
 */
export function createEntityStandardColumns(
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  const entityName = terminology?.singular || 'entity'
  const EntityName = entityName.charAt(0).toUpperCase() + entityName.slice(1)

  return [
    {
      id: 'name',
      name: 'name' as keyof Entity, // Type-safe key reference
      label: `${EntityName} Name`,
      description: `Name of the ${entityName}`,
      isVisible: true,
      isRequired: true,
      isSystem: true, // Cannot be disabled
      isFixed: true, // This column cannot be moved
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      ...ENTITY_COLUMN_WIDTHS.VERY_WIDE,
    },
    {
      id: 'email',
      name: 'email' as keyof Entity, // Type-safe key reference
      label: 'Email',
      description: `Email address of the ${entityName}`,
      isVisible: true,
      isRequired: false, // Email is nullable in the database schema
      isSystem: false,
      isFixed: false,
      order: 1,
      fieldType: FieldTypeEnum.enum.STRING,
      ...ENTITY_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'phone',
      name: 'phone' as keyof Entity, // Type-safe key reference
      label: 'Phone',
      description: `Phone number of the ${entityName}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 2,
      fieldType: FieldTypeEnum.enum.STRING,
      ...ENTITY_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'website',
      name: 'website' as keyof Entity, // Type-safe key reference
      label: 'Website',
      description: `Website URL of the ${entityName}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 3,
      fieldType: FieldTypeEnum.enum.STRING,
      ...ENTITY_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'lifecycleStage',
      name: 'lifecycleStage' as keyof Entity,
      label: 'Lifecycle Stage',
      description: `Relationship lifecycle stage of the ${entityName} (prospect / active / churned). Org-customizable.`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 4,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      // Defaults; actual options are fetched from CrmStage (moduleType=ENTITY_LIFECYCLE)
      options: DEFAULT_ENTITY_LIFECYCLE_STAGES.filter(s => !s.isHidden).map(
        stage => ({
          value: stage.id,
          label: stage.name,
          color: stage.color,
        }),
      ),
      ...ENTITY_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'entityType',
      name: 'entityType' as keyof Entity, // Type-safe key reference
      label: 'Type',
      description: `Type/category of the ${entityName}`,
      isVisible: false, // Hidden by default, user can enable it
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 4.5,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...ENTITY_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'projectCount',
      name: '_count.projects' as keyof Entity, // Type-safe key reference for count
      label: 'Projects',
      description: `Number of projects for this ${entityName}`,
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 5,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...ENTITY_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'members',
      name: 'members' as keyof Entity,
      label: 'Members',
      description: `${EntityName} team members`,
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 5.5, // Between projectCount and tags
      fieldType: 'ENTITY_MEMBERS' as FieldTypeEnum, // Special field type for entity members display
      ...ENTITY_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'tags',
      name: 'tags' as keyof Entity,
      label: 'Tags',
      description: `Tags for organizing and categorizing the ${entityName}`,
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 90, // Positioned near end of standard columns, custom fields come after at 91+
      fieldType: FieldTypeEnum.enum.STRING,
      ...ENTITY_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'address',
      name: 'address' as keyof Entity, // Type-safe key reference
      label: 'Address',
      description: `Address details of the ${entityName}`,
      isVisible: true, // Make visible by default for InfoPanel
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 6,
      fieldType: FieldTypeEnum.enum.TEXT,
      ...ENTITY_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'logoUrl',
      name: 'logoUrl' as keyof Entity, // Type-safe key reference
      label: 'Logo',
      description: `Logo image for the ${entityName}`,
      isVisible: true, // Make visible by default for InfoPanel
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 7,
      fieldType: FieldTypeEnum.enum.FILE,
      ...ENTITY_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'createdById',
      name: 'createdById' as keyof Entity, // Type-safe key reference
      label: 'Created By',
      description: `User who created this ${entityName}`,
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 7.5,
      fieldType: FieldTypeEnum.enum.USER,
      ...ENTITY_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'createdAt',
      name: 'createdAt' as keyof Entity, // Type-safe key reference
      label: 'Created Date',
      description: `Date when ${entityName} was created`,
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 8,
      fieldType: FieldTypeEnum.enum.DATETIME,
      ...ENTITY_COLUMN_WIDTHS.MEDIUM,
    },
  ]
}

/**
 * Default standard column definitions for the Entity module
 * Uses default 'entity' terminology
 */
export const ENTITY_STANDARD_COLUMNS: ModuleColumnDefinition[] =
  createEntityStandardColumns()

/**
 * Helper function to get all available columns for a module,
 * combining standard and custom fields
 */
export function getEntityColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  // Start with standard columns using the provided terminology
  const standardColumns = createEntityStandardColumns(terminology).map(
    column => ({
      ...column,
      // Override visibility if preferences exist
      isVisible:
        visibilityPreferences?.[column.id] !== undefined
          ? !!visibilityPreferences[column.id] // Ensure boolean type with !!
          : column.isVisible,
    }),
  )

  // Default width for custom field columns based on field type using standardized widths
  const getCustomFieldWidth = (
    fieldType: string,
  ): { width: string; minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return ENTITY_COLUMN_WIDTHS.WIDE
      case FieldTypeEnum.enum.TEXT:
        return ENTITY_COLUMN_WIDTHS.WIDE
      case FieldTypeEnum.enum.NUMBER:
        return ENTITY_COLUMN_WIDTHS.NARROW
      case FieldTypeEnum.enum.BOOLEAN:
        return ENTITY_COLUMN_WIDTHS.NARROW
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return ENTITY_COLUMN_WIDTHS.MEDIUM
      case FieldTypeEnum.enum.DROPDOWN:
        return ENTITY_COLUMN_WIDTHS.MEDIUM
      case FieldTypeEnum.enum.FILE:
        return ENTITY_COLUMN_WIDTHS.WIDE
      case FieldTypeEnum.enum.USER:
        return ENTITY_COLUMN_WIDTHS.MEDIUM
      default:
        return ENTITY_COLUMN_WIDTHS.MEDIUM
    }
  }

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType)

    // Parse dropdown options from settings if available
    const fieldOptions =
      (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT') &&
      field.settings?.options &&
      Array.isArray(field.settings.options)
        ? field.settings.options.map((opt: any) => {
            if (typeof opt === 'string') return { value: opt, label: opt }
            return {
              value: String(opt.value || opt.id || opt.label || ''),
              label: String(opt.label || opt.name || opt.value || ''),
            }
          })
        : undefined

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`] // Ensure boolean type with !!
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    }
  }) as ModuleColumnDefinition[]

  // Combine both types of columns
  return [...standardColumns, ...customColumns]
}

/**
 * Get module type for column definitions
 */
export function getEntityModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.ENTITY
}
